import { Building, Shield, Cloud, Network, BarChart, Monitor, ChevronDown } from "lucide-react";
import { useState } from "react";

const capabilities = [
  {
    title: "Enterprise Architecture",
    description: "End-to-end solutions that drive digital transformation and sustainable business growth.",
    details: [
      "Digital Strategy & Roadmap Development",
      "Enterprise System Integration",
      "Legacy System Modernization",
      "Microservices Architecture",
      "API Management & Development",
    ],
    icon: Building,
    iconBg: "bg-sky-100 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400",
  },
  {
    title: "Security Services",
    description: "Comprehensive security posture management to protect assets and maintain compliance.",
    details: [
      "Threat Detection & Response",
      "Security Assessment & Auditing",
      "Identity & Access Management",
      "Data Protection & Privacy",
      "Security Operations Center (SOC)",
    ],
    icon: Shield,
    iconBg: "bg-indigo-100 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
    partner: { name: "Palo Alto Networks", url: "https://www.paloaltonetworks.com", logo: "https://www.paloaltonetworks.com/favicon.ico" },
  },
  {
    title: "Cloud & Data Center",
    description: "Modern cloud infrastructure and data center solutions for optimal performance.",
    details: [
      "Cloud Migration Strategy",
      "Multi-Cloud Management",
      "Data Center Optimization",
      "Cloud Security & Compliance",
      "Disaster Recovery Planning",
    ],
    icon: Cloud,
    iconBg: "bg-violet-100 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400",
    partners: [
      { name: "AWS", url: "https://aws.amazon.com", logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg" },
      { name: "Google Cloud", url: "https://cloud.google.com", logo: "https://upload.wikimedia.org/wikipedia/commons/0/01/Google-cloud-platform.svg" },
      { name: "Azure", url: "https://azure.microsoft.com", logo: "https://upload.wikimedia.org/wikipedia/commons/a/a8/Microsoft_Azure_Logo.svg" },
      { name: "Oracle Cloud", url: "https://www.oracle.com/cloud", logo: "https://upload.wikimedia.org/wikipedia/commons/5/50/Oracle_logo.svg" },
    ],
  },
  {
    title: "Network Solutions",
    description: "Advanced networking for seamless connectivity, low latency, and high availability.",
    details: [
      "Network Architecture Design",
      "SD-WAN Implementation",
      "Network Security & Segmentation",
      "Performance Optimization",
      "Wireless & Mobile Solutions",
    ],
    icon: Network,
    iconBg: "bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    partner: { name: "Ubiquiti", url: "https://ui.com", logo: "https://ui.com/favicon.ico" },
  },
  {
    title: "Analytics & AI",
    description: "Data-driven insights and AI/ML solutions to power intelligent decision-making.",
    details: [
      "Business Intelligence",
      "Machine Learning Models",
      "Predictive Analytics",
      "Data Visualization",
      "AI-Powered Automation",
    ],
    icon: BarChart,
    iconBg: "bg-orange-100 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400",
  },
  {
    title: "Digital Workspace",
    description: "Modern workplace solutions that enhance productivity, collaboration, and employee experience.",
    details: [
      "Remote Work Infrastructure",
      "Collaboration Platforms",
      "Digital Employee Experience",
      "Workplace Analytics",
      "Mobile Device Management",
    ],
    icon: Monitor,
    iconBg: "bg-pink-100 dark:bg-pink-500/10 text-pink-600 dark:text-pink-400",
  },
];

export const Capabilities = () => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <section id="capabilities" className="py-24 px-4 bg-slate-50 dark:bg-slate-950">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-16 space-y-4">
          <span className="inline-block text-sm font-medium px-4 py-1.5 rounded-full bg-sky-100 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-200 dark:border-sky-500/20">
            Capabilities
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white">
            Our Capabilities
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Deep expertise across the full technology stack — from strategy and architecture to
            implementation and ongoing operations.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {capabilities.map((cap) => {
            const Icon = cap.icon;
            const isOpen = expandedId === cap.title;
            return (
              <div
                key={cap.title}
                className={`bg-white dark:bg-slate-800 border rounded-2xl p-8 transition-all duration-300 ${
                  isOpen
                    ? "border-sky-300 dark:border-sky-500/50 shadow-lg shadow-sky-500/10"
                    : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md"
                }`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${cap.iconBg}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  {"partner" in cap && cap.partner && (
                    <a
                      href={cap.partner.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:border-sky-400 transition-colors"
                    >
                      <img src={cap.partner.logo} alt={cap.partner.name} className="w-4 h-4 rounded-sm" />
                      <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{cap.partner.name} Partner</span>
                    </a>
                  )}
                </div>
                {"partners" in cap && cap.partners && (
                  <div className="flex flex-wrap gap-2 mb-5">
                    {cap.partners.map((p) => (
                      <a
                        key={p.name}
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:border-sky-400 transition-colors"
                      >
                        <img src={p.logo} alt={p.name} className="w-4 h-4 object-contain" />
                        <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{p.name}</span>
                      </a>
                    ))}
                  </div>
                )}
                <h3 className="text-xl font-semibold mb-3 text-slate-900 dark:text-white">
                  {cap.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                  {cap.description}
                </p>

                <button
                  onClick={() => setExpandedId(isOpen ? null : cap.title)}
                  className="flex items-center gap-1.5 text-sm font-medium text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 transition-colors"
                >
                  {isOpen ? "Show Less" : "Explore"}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {isOpen && (
                  <ul className="mt-5 space-y-2.5 border-t border-slate-100 dark:border-slate-700 pt-5 animate-in slide-in-from-top-2 duration-200">
                    {cap.details.map((d) => (
                      <li key={d} className="flex items-start gap-2.5 text-sm text-slate-600 dark:text-slate-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-sky-500 flex-shrink-0 mt-1.5" />
                        {d}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
